import { tracking } from '@iwoca/frontend-tracking-library';
import { Button } from '@iwoca/orion';
import { REQUEST_CHANGE_ROUTER } from '@iwRouter/IwRouter.actions';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import { buildLocationWithSearch } from '../../../drawDown/DrawDownHelper';
import { FormalOffer } from '../../../myOffers/MyOffers.types';
import styles from '../FundingSteps.module.css';

const mapDispatchToProps = (dispatch: Dispatch) => ({
    redirectToConfirm: ({
        offerId,
        amount,
        sweep
    }: {
        offerId: FormalOffer['offer_id'];
        amount: number;
        sweep?: string;
    }) => {
        dispatch({
            type: REQUEST_CHANGE_ROUTER,
            //@ts-ignore
            location: buildLocationWithSearch({
                pathname: '/confirm-funding/',
                offerId,
                amount,
                sweep
            })
        });
    }
});

const Caption = ({
    isInterestOnlyExtension,
    isAccessUser
}: {
    isInterestOnlyExtension?: boolean;
    isAccessUser: boolean;
}) => {
    if (isAccessUser) {
        return (
            <FormattedMessage
                id="_.funding_requirements.all_requirements_completed_access_user_caption"
                defaultMessage="The remaining steps will be completed by the applicant."
            />
        );
    }

    if (isInterestOnlyExtension) {
        return (
            <FormattedMessage
                id="_.funding_requirements.all_requirements_completed_interestOnlyCaption_caption"
                defaultMessage="Click ‘{next}’ to extend your loan. {br} The loan will not be extended until you click ‘Confirm’ in the next step."
                values={{
                    br: <br />,
                    next: (
                        <span className={styles.highlightedAction}>
                            <FormattedMessage
                                id="_.funding_requirements.all_requirements_completed_action"
                                defaultMessage="Next"
                            />
                        </span>
                    )
                }}
            />
        );
    }

    return (
        <FormattedMessage
            id="_.funding_requirements.all_requirements_completed_caption"
            defaultMessage="Click ‘{next}’ to go to confirm your drawdown. {br} We won’t send the funds to you until you hit ‘Confirm’ on the next step."
            values={{
                br: <br />,
                next: (
                    <span className={styles.highlightedAction}>
                        <FormattedMessage
                            id="_.funding_requirements.all_requirements_completed_action"
                            defaultMessage="Next"
                        />
                    </span>
                )
            }}
        />
    );
};

const AllRequirementsCompletedStep = ({
    offerId,
    amount,
    sweep,
    redirectToConfirm,
    isInterestOnlyExtension,
    isAccessUser
}: {
    offerId: FormalOffer['offer_id'];
    amount: number;
    sweep?: string;
    isInterestOnlyExtension?: boolean;
    isAccessUser: boolean;
} & ReturnType<typeof mapDispatchToProps>) => {
    return (
        <div className={styles.stepContainer}>
            <div className={styles.stepMessages}>
                <span className={styles.title}>
                    {isAccessUser ? (
                        <FormattedMessage
                            id="_.funding_requirements.all_requirements_completed_access_user_title"
                            defaultMessage="You're all set!"
                        />
                    ) : (
                        <FormattedMessage
                            id="_.funding_requirements.all_requirements_completed_title"
                            defaultMessage="You've completed all the steps!"
                        />
                    )}
                </span>
                <span className={styles.caption}>
                    <Caption
                        isInterestOnlyExtension={isInterestOnlyExtension}
                        isAccessUser={isAccessUser}
                    />
                </span>
            </div>
            {!isAccessUser && (
                <Button
                    variant="success"
                    className={styles.actionButton}
                    onClick={() => {
                        tracking.ampli.fundingRequirementsConfirmClicked({
                            progress: {},
                            version: '1.0'
                        });
                        redirectToConfirm({ offerId, amount, sweep });
                    }}
                    data-action-name="Next to confirm funding"
                >
                    <FormattedMessage
                        id="_.funding_requirements.all_requirements_completed_action"
                        defaultMessage="Next"
                    />
                </Button>
            )}
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(null, mapDispatchToProps)(AllRequirementsCompletedStep);
